import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building, Home } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import CompanyForm from '@/components/CompanyForm';
import { CompanyFormValues } from '@/components/company/CompanyFormSchema';
import { supabase } from '@/integrations/supabase/client';

enum SignupStep {
  Credentials,
  CompanyInfo
}

const CompanySignup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<SignupStep>(SignupStep.Credentials);
  const [companyData, setCompanyData] = useState<CompanyFormValues | null>(null);
  
  const { signUp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Handle the first step submission (credentials)
  const handleNextStep = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    if (!email || !password || !companyName) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields to continue.",
        variant: "destructive"
      });
      return;
    }

    // Move to company info step
    setCurrentStep(SignupStep.CompanyInfo);
  };

  // Handle the final submission with company information
  const handleCompanyInfoSubmit = async (data: CompanyFormValues) => {
    setLoading(true);
    setCompanyData(data);

    try {
      // First create the company in the database
      const { data: companyResult, error: companyError } = await supabase
        .from('companies')
        .insert({
          name: companyName,
          locations: [data.location],
          bio: data.bio,
          staff_size: data.staffSize,
          website: data.website,
          twitter_url: data.twitterUrl,
          instagram_url: data.instagramUrl,
          linkedin_url: data.linkedinUrl,
          is_visible: true
        })
        .select('id')
        .single();

      if (companyError) {
        throw new Error(`Failed to create company: ${companyError.message}`);
      }

      // Now sign up the user with the company ID
      await signUp(email, password, { 
        is_company: true, 
        company_name: companyName,
        company_id: companyResult.id,
        user_type: 'company'
      });
      
      toast({
        title: "Company account created",
        description: "Please check your email to confirm your account."
      });
      
      navigate('/company-login');
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle going back to credentials step
  const handleBackToCredentials = () => {
    setCurrentStep(SignupStep.Credentials);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <div className="p-2 bg-redflags-red/10 rounded-full">
                <Building className="w-8 h-8 text-redflags-red" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-redflags-dark">
              Register Your Company
            </h1>
            <p className="text-redflags-gray mt-2">
              {currentStep === SignupStep.Credentials 
                ? "Create an account to manage your company profile" 
                : "Tell us more about your company"}
            </p>
            
            {currentStep === SignupStep.CompanyInfo && (
              <div className="mt-2 text-sm text-redflags-gray">
                Step 2 of 2
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            {currentStep === SignupStep.Credentials ? (
              <form onSubmit={handleNextStep} className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Your company name"
                    required
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="company@example.com"
                    required
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
                
                <Button type="submit" className="w-full">
                  Next
                </Button>
              </form>
            ) : (
              <CompanyForm 
                onSubmit={handleCompanyInfoSubmit}
                onCancel={handleBackToCredentials}
                submitButtonText="Register Company"
                initialValues={{ name: companyName, location: "", bio: "", staffSize: "" }}
                hideCancel={false}
              />
            )}
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              <span>Already registered?</span>{' '}
              <Link to="/company-login" className="text-redflags-red hover:underline">
                Log in to your company account
              </Link>
            </p>
          </div>
          
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={() => navigate('/')}
            >
              <Home className="w-4 h-4" />
              Return to Home
            </Button>
          </div>
          
          <footer className="mt-10 pt-6 text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} RedFlags. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default CompanySignup;
